// Client for the sm4rt-eset-integration Azure Functions service. Covers the Phase 4 admin
// API (per-customer ESET licenses + edit) and the onboarding provisioning endpoints the
// ESET management surface drives.
// Calls go to sm4rt-api's staff-authenticated passthrough (/api/eset-int/* ->
// sm4rt-eset-integration /api/*), which adds the function key server-side so the browser
// never holds it. Dev: the CRA '/api' proxy sends this to sm4rt-api on :7071, which
// forwards to eset-integration on :7082 (ESET_INTEGRATION_BASE_URL).

const API_BASE_URL = '/api/eset-int';

// cr001_billing_cadence choice values (mirror of the backend optionset).
export const BILLING_CADENCE_MONTHLY = 468190000;
export const BILLING_CADENCE_ANNUAL = 468190001;

/** The linked renewal invoice's state (present only when one is bound). */
export interface EsetInvoiceRef {
  number: string | null;
  is_paid: boolean | null;
  due_date: string | null;
  rs_id: string | null;
}

/** Authoritative billing-status chip value (backend `_billing_status`). `unpaid_years` (the
 *  historical "Unpaid · N yr" reconcile chip) is a separate signal, not one of these. */
export type EsetBillingStatus =
  | 'cancelled' | 'lapsed'
  | 'trial' | 'overdue' | 'awaiting_payment' | 'unbilled' | 'due_soon' | 'current';

/** One bundle line of a renewal line-up (e.g. 1x RSub.AV.ESET.3D1Y @ 150). */
export interface EsetLineupLine {
  sku: string;
  quantity: number;
  price: number | null;
}

/** "1x 3D + 1x 1D" — the line-up as a compact label (SKU tier only). */
export function lineupLabel(lineup?: EsetLineupLine[]): string {
  if (!lineup || lineup.length === 0) return '';
  return lineup
    .map((l) => `${l.quantity}x ${(l.sku.match(/\d+D\d+Y$/) || [l.sku])[0].replace(/1Y$/, '')}`)
    .join(' + ');
}

/** One ESET license/subscription (backend ManagedSubscription.to_admin_dict). */
export interface EsetSubscription {
  id: string;
  license_ref: string | null;
  name: string | null;
  product_code: string | null;
  product_name: string | null;
  product_id: string | null; // cr001_products GUID (the billing bundle)
  product_bundle: string | null; // cr001_products name/SKU resolved from product_id (e.g. RSub.AV.ESET.1D1Y)
  // Commercial product model (backend DESIGN §5g): product + class come from the SKU (its {N}D is
  // ignored); the sub bills its ESET licensed units as 3D bundles + a 1D/2D remainder.
  commercial_product: string | null; // 'ESET PROTECT Entry' | 'ESET PROTECT Advanced'
  billing_class: string | null; // 'Residential' | 'Business'
  licensed_units: number; // ESET licensed units (= quantity) — what the sub bills for, never usage
  billed_units: number | null; // units billed for the current term (null: trial / monthly / no stamp yet)
  billed_seats: number | null; // legacy alias of billed_units
  seat_variance: boolean; // licensed_units !== billed_units (extra seats to charge, or a drop)
  term_years: number; // renewal term (default 1; a 2-year term bills the 1-year line-up twice)
  lineup?: EsetLineupLine[]; // the bundles one renewal term bills (annual/term subs)
  renewal_amount?: number; // one term's total for the line-up
  lineup_error?: string; // a bundle the line-up needs isn't catalogued
  product_needs_review?: boolean; // default product set on trial conversion — confirm product + class
  quantity: number;
  usage: number;
  trial: boolean;
  trial_expiration: string | null;
  vendor_state: number | null;
  status: string | null;
  cancelled?: boolean; // an operator cancelled it — manage no longer bills it
  removed_from_vendor?: string | null; // ISO date the licence was permanently removed from ESET
  end_date?: string | null;
  suspended: boolean;
  action_on_expiry: string | null;
  expiry_date: string | null;
  days_until_expiry: number | null;
  next_billing_date: string | null; // annual invoice-issue date (= derived expiry − 30d); null if no derivable start
  billing_status: EsetBillingStatus | null; // authoritative chip state (backend-derived)
  first_created: string | null; // createdon (when we first synced the row)
  start_date: string | null; // real subscription start (from ESET licence history); UI falls back to first_created
  warning_sent_date: string | null;
  warning_gap: boolean;
  invoice_gap: boolean;
  invoice?: EsetInvoiceRef;
}

/** Per-customer ESET account header shown above the license list. */
export interface EsetCustomerHeader {
  managed_customer_id: string;
  vendor_account_id: string | null;
  name: string | null;
  billing_cadence: number | null;
  installer_url: string | null;
  installer_url_expiry: string | null;
  status: string | null;
}

export interface EsetSubscriptionsList {
  customer_guid: string;
  customer: EsetCustomerHeader | null;
  count: number;
  subscriptions: EsetSubscription[];
}

export interface EsetSubscriptionUpdate {
  product_id?: string; // rebind the SKU (sets commercial product + class; cr001_products GUID)
  action_on_expiry?: string;
  term_years?: number; // 1–3
}

/** Result of a provisioning action (create customer / order license / installer). */
export interface EsetProvisionResult {
  status: string;
  [key: string]: unknown;
}

/**
 * An error from the ESET integration API. Carries the HTTP `status` plus any
 * machine-readable fields the backend returned — `retryable` marks a transient
 * condition the caller should present as a soft warning + retry (e.g. the
 * `group_not_ready` case where a just-provisioned installer isn't available for
 * a minute or two), not a hard failure. `message` is the clean backend message.
 */
export class EsetApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly retryable: boolean;

  constructor(status: number, message: string, opts?: { code?: string; retryable?: boolean }) {
    super(message);
    this.name = 'EsetApiError';
    this.status = status;
    this.code = opts?.code;
    this.retryable = opts?.retryable ?? false;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const message =
      body?.error || (await response.text().catch(() => response.statusText)) || response.statusText;
    throw new EsetApiError(response.status, message, {
      code: typeof body?.code === 'string' ? body.code : undefined,
      retryable: Boolean(body?.retryable),
    });
  }
  return response.json();
}

// --- Phase 4 admin API ---------------------------------------------------- //

/** A customer's ESET licenses + account header. `customer` is the Dataverse GUID. */
export function listCustomerSubscriptions(
  customerGuid: string,
  signal?: AbortSignal,
): Promise<EsetSubscriptionsList> {
  return request<EsetSubscriptionsList>(
    `/subscriptions?customer=${encodeURIComponent(customerGuid)}`,
    { method: 'GET', signal },
  );
}

/** One row of the all-customers admin overview table (EsetSubscription + the extra
 *  cross-customer columns the ?all=true endpoint derives). */
export interface EsetOverviewRow extends EsetSubscription {
  customer_name: string | null;
  last_billed: string | null;   // annual: bound invoice date · monthly: last schedule write
  next_billing: string | null;  // annual: expiry − 30d · monthly: RS schedule next_run
}

/** Every ESET subscription across all customers (admin overview table). */
export function listAllSubscriptions(
  signal?: AbortSignal,
): Promise<{ vendor: string; count: number; subscriptions: EsetOverviewRow[] }> {
  return request(`/subscriptions?all=true`, { method: 'GET', signal });
}

// --- Unpaid years (never-billed anniversary terms) + reconcile ------------ //

/** A never-billed completed anniversary term (annual/term subs only). */
export interface EsetUnpaidPeriod {
  start: string;              // ISO term start (also the decision key)
  end: string;                // ISO term end (inclusive)
}

/** A sub with one or more unpaid years — from GET ?unpaid_years=true. */
export interface EsetUnpaidRow {
  id: string;
  license_ref: string | null;
  customer_name: string | null;
  product_bundle: string | null;
  start_date: string | null;
  unpaid_years: number;
  periods: EsetUnpaidPeriod[];
}

/** Annual/term subs with years never billed — the reconcile candidates. */
export function getUnpaidYears(
  signal?: AbortSignal,
): Promise<{ vendor: string; count: number; subscriptions: EsetUnpaidRow[] }> {
  return request(`/subscriptions?unpaid_years=true`, { method: 'GET', signal });
}

export type ReconcileAction = 'bill' | 'waive';

export interface EsetReconcileResult {
  status: string;             // reconciled | dry_run | billing_disabled | none | not_applicable | no_product
  billed?: number;
  waived?: number;
  periods?: Array<EsetUnpaidPeriod & { action: ReconcileAction }>;
  invoice_id?: number | string | null;
  dry_run?: boolean;
  billing_enabled?: boolean;
}

/** Reconcile a sub's unpaid years: `decisions` maps each ISO term start to 'bill' | 'waive',
 *  or pass `action` to apply one to all. A real run needs ENABLE_BILLING; dry_run previews. */
export function reconcileUnpaidYears(
  id: string,
  body: { decisions?: Record<string, ReconcileAction>; action?: ReconcileAction; dry_run: boolean },
): Promise<EsetReconcileResult> {
  return request(`/subscriptions/${encodeURIComponent(id)}/reconcile-unpaid-years`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// --- Raise renewal invoice (bill due anniversary terms) ------------------- //

/** One anniversary term the raise-invoice flow can bill. */
export interface EsetRaiseInvoicePeriod {
  start: string;    // ISO term start (also the selection key)
  end: string;      // ISO term end (inclusive)
  amount: number;   // line amount for this term
  selected: boolean;
}

/** Result of GET/POST /subscriptions/{id}/raise-invoice. */
export interface EsetRaiseInvoiceResult {
  status: string;   // dry_run | billing_disabled | none_selected | raised | up_to_date | no_start | no_lineup | failed
  periods: EsetRaiseInvoicePeriod[];
  total: number;
  selected_count: number;
  units?: number; // ESET licensed units each term bills
  lineup?: EsetLineupLine[]; // the bundles one term bills (3D + 1D/2D remainder)
  mode?: 'invoice' | 'schedule'; // standalone invoice vs a one-off line on the monthly schedule
  invoice_id?: number | string | null;      // RepairShopr invoice id (for the RS deep link)
  invoice_number?: number | string | null;  // human invoice number (shown + the SM4RT invoice link)
  error?: string | null;                     // failure reason when status === 'failed'
  dry_run: boolean;
  billing_enabled: boolean;
}

/** Raise the renewal invoice for a sub's due anniversary terms. `terms` = the ISO term-starts
 *  to bill (omit = all due). A real run (dry_run:false) needs ENABLE_BILLING; dry_run previews. */
export function raiseInvoice(
  id: string,
  body: { dry_run: boolean; terms?: string[] },
): Promise<EsetRaiseInvoiceResult> {
  return request(`/subscriptions/${encodeURIComponent(id)}/raise-invoice`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Result of POST /subscriptions/{id}/void-invoice — undo an erroneously-raised renewal
 *  invoice (delete in RepairShopr/Xero + drop the mirror + unbind + clear billing guards). */
export interface EsetVoidInvoiceResult {
  status: string;   // voided | dry_run | no_invoice | paid | failed
  subscription_id: string;
  license_ref?: string | null;
  invoice_number?: number | string | null;
  invoice_rs_id?: number | string | null;
  invoice_paid?: boolean;
  dry_run: boolean;
  error?: string;
}

/** Void the renewal invoice bound to a sub. A real run (dry_run:false) deletes it everywhere;
 *  dry_run previews what would be voided (invoice number, paid state). */
export function voidInvoice(
  id: string,
  body: { dry_run: boolean },
): Promise<EsetVoidInvoiceResult> {
  return request(`/subscriptions/${encodeURIComponent(id)}/void-invoice`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface EsetCancelResult {
  status: string; // cancelled | removed | suspended | reinstated | dry_run | already_ended | not_cancelled | remove_not_allowed | failed
  subscription_id: string;
  license_ref?: string | null;
  unpaid_invoice?: number | string | null; // a bound renewal invoice still unpaid (void separately)
  suspend_license?: boolean;
  remove_license?: boolean;
  already_removed?: boolean;
  current_status?: string | null;
  dry_run: boolean;
  error?: string;
}

/** Cancel a sub so manage stops billing it; in ESET optionally suspend the licence or remove it
 *  permanently (`remove_license` also works on an already-cancelled sub). */
export function cancelSubscription(
  id: string,
  body: { dry_run: boolean; suspend_license?: boolean; remove_license?: boolean },
): Promise<EsetCancelResult> {
  return request(`/subscriptions/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Undo a cancel (manage bills it again). Doesn't unsuspend the licence in ESET. */
export function reinstateSubscription(id: string, body: { dry_run: boolean }): Promise<EsetCancelResult> {
  return request(`/subscriptions/${encodeURIComponent(id)}/reinstate`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** One event on a subscription's change timeline. */
export interface EsetTimelineEvent {
  date: string | null;
  type: string;
  summary: string;
  detail: string | null;
  is_trial: boolean;
  source: string; // lifecycle | provisioning | seed | eset_history | invoice
}

export interface EsetTimeline {
  subscription_id: string;
  customer_name: string | null;
  license_ref: string | null;
  include_trials: boolean;
  include_expiry: boolean;
  count: number;
  events: EsetTimelineEvent[];
}

/** A subscription's lifecycle change timeline (the drill-down). Trial events are excluded
 *  unless includeTrials; synthetic "due to expire" markers are added when includeExpiry
 *  (both admin toggles). */
export function getSubscriptionTimeline(
  id: string,
  includeTrials = false,
  includeExpiry = false,
  signal?: AbortSignal,
): Promise<EsetTimeline> {
  const params = new URLSearchParams();
  if (includeTrials) params.set('include_trials', 'true');
  if (includeExpiry) params.set('include_expiry', 'true');
  const q = params.toString();
  return request(`/subscriptions/${encodeURIComponent(id)}/timeline${q ? `?${q}` : ''}`, {
    method: 'GET',
    signal,
  });
}

/** Summary returned by a manage/sync batch run (shape varies by job). */
export type EsetBatchResult = { dry_run?: boolean } & Record<string, unknown>;

/** Run the renewal/billing lifecycle batch (POST /api/manage). Only a REAL run
 *  (dryRun=false) performs actions / emits timeline events. */
export function runManage(dryRun: boolean): Promise<EsetBatchResult> {
  return request(`/manage`, { method: 'POST', body: JSON.stringify({ dry_run: dryRun }) });
}

/** Run the ESET → Dataverse reconciliation batch (POST /api/sync). */
export function runSync(dryRun: boolean): Promise<EsetBatchResult> {
  return request(`/sync`, { method: 'POST', body: JSON.stringify({ dry_run: dryRun }) });
}

export function getSubscription(id: string, signal?: AbortSignal): Promise<EsetSubscription> {
  return request<EsetSubscription>(`/subscriptions/${encodeURIComponent(id)}`, {
    method: 'GET',
    signal,
  });
}

export function updateSubscription(
  id: string,
  updates: EsetSubscriptionUpdate,
): Promise<EsetSubscription> {
  return request<EsetSubscription>(`/subscriptions/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export function sendRenewalWarning(id: string): Promise<Record<string, unknown>> {
  return request(`/subscriptions/${encodeURIComponent(id)}/send-warning`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function raiseRenewalInvoice(id: string): Promise<Record<string, unknown>> {
  return request(`/subscriptions/${encodeURIComponent(id)}/raise-invoice`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// --- Onboarding / provisioning ------------------------------------------- //

/** Create the ESET company for a SM4RT customer (customerId = cr001_id). */
export function provisionCustomer(
  customerId: string,
  overrides?: Record<string, unknown>,
): Promise<EsetProvisionResult> {
  return request<EsetProvisionResult>('/customers', {
    method: 'POST',
    body: JSON.stringify({ customer_id: customerId, ...(overrides ? { overrides } : {}) }),
  });
}

export interface OrderLicenseInput {
  customerId: string;
  productCode: number | string;
  quantity?: number;
  /** The RS billing bundle reference — CatalogProduct.productId (the RS product id).
   *  The backend resolves it (RS id or GUID) to the cr001_products GUID. */
  productGuid?: string;
}

/** Order a trial license (licenseType 4). */
export function orderTrial(input: OrderLicenseInput): Promise<EsetProvisionResult> {
  return request<EsetProvisionResult>('/trials', { method: 'POST', body: _licenseBody(input) });
}

/** Order a paid license (licenseType 1). */
export function orderLicense(input: OrderLicenseInput): Promise<EsetProvisionResult> {
  return request<EsetProvisionResult>('/licenses', { method: 'POST', body: _licenseBody(input) });
}

function _licenseBody(input: OrderLicenseInput): string {
  return JSON.stringify({
    customer_id: input.customerId,
    product_code: input.productCode,
    quantity: input.quantity ?? 1,
    ...(input.productGuid ? { product_guid: input.productGuid } : {}),
  });
}

/** Generate a per-customer installer download URL (ESET Connect). */
export function generateInstaller(
  customerId: string,
  osFamily?: number,
): Promise<EsetProvisionResult> {
  return request<EsetProvisionResult>('/installers', {
    method: 'POST',
    body: JSON.stringify({ customer_id: customerId, ...(osFamily ? { os_family: osFamily } : {}) }),
  });
}

export interface EsetProduct {
  code: string | number;
  name: string;
}

/** The vendor's available ESET products (for the order form's product_code picker). */
export function listEsetProducts(signal?: AbortSignal): Promise<{ products: EsetProduct[] }> {
  return request<{ products: EsetProduct[] }>('/products', { method: 'GET', signal });
}
